export interface AdCreativeApprovedAudioCandidateUploadFields {
  targetLocale: string;
  slotKey: string;
  captionArtifactId: string;
  voiceRightsEvidenceId: string;
}

export interface AdCreativeApprovedAudioCandidateView {
  evidenceId: string;
  status: 'review_required';
  runnable: false;
  creativeItemId: string;
  slotKey: string;
  targetLocale: string;
  approvedVoiceId: string;
  captionArtifactId: string;
  voiceRightsEvidenceId: string;
  audio: {
    assetId: string;
    byteSize: number;
    mimeType: 'audio/wav' | 'audio/mp4';
    codec: 'pcm_s16le' | 'pcm_s24le' | 'pcm_f32le' | 'aac';
    durationMs: number;
    sampleRateHz: number;
    channels: number;
  };
  review:
    | {
        status: 'required';
        blockingReasons: ['language_unverified', 'voice_identity_unverified'];
      }
    | {
        status: 'approved';
        reviewId: string;
        reviewedAt: string;
        artifactId?: string;
      }
    | {
        status: 'rejected';
        reviewId: string;
        reviewedAt: string;
        rejectionReason: string;
      };
  createdAt: string;
}

export interface AdCreativeApprovedAudioCandidateUploadResponse {
  success: boolean;
  data?: {
    candidate: AdCreativeApprovedAudioCandidateView;
    reused: boolean;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeApprovedAudioCandidateListResponse {
  success: boolean;
  data?: {
    candidates: AdCreativeApprovedAudioCandidateView[];
    limit: number;
    hasMore: boolean;
    nextCursor?: string;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeApprovedAudioCandidateReadResponse {
  success: boolean;
  data?: {
    candidate: AdCreativeApprovedAudioCandidateView;
  };
  error?: string;
  code?: string;
}

export interface AdCreativeApprovedAudioCandidateReviewFields {
  decision: 'approve' | 'reject';
  confirmations?: {
    exactPlaybackConfirmed: true;
    targetLanguageConfirmed: true;
    approvedVoiceConfirmed: true;
    captionTimingWithin250MsConfirmed: true;
  };
  rejectionReason?: string;
}

export interface AdCreativeApprovedAudioCandidateReviewResponse {
  success: boolean;
  data?: {
    candidate: AdCreativeApprovedAudioCandidateView;
    review: AdCreativeApprovedAudioCandidateView['review'];
    artifact?: {
      artifactId: string;
      status: 'verified';
      targetLocale: string;
      approvedVoiceId: string;
      audioAssetId: string;
    };
    reusedReview: boolean;
    reusedArtifact: boolean;
    reusedPromotion: boolean;
  };
  error?: string;
  code?: string;
}

export function buildAdCreativeApprovedAudioCandidateListPath(
  projectId: string,
  creativeItemId: string,
  options: { limit?: number; cursor?: string } = {},
): string {
  const params = new URLSearchParams({ projectId });
  if (options.limit !== undefined) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  return `/api/agent/ad-creative/items/${encodeURIComponent(creativeItemId)}`
    + `/video-audio-candidates?${params.toString()}`;
}

export function buildAdCreativeApprovedAudioCandidateReadPath(
  projectId: string,
  creativeItemId: string,
  candidateId: string,
): string {
  const params = new URLSearchParams({ projectId });
  return `/api/agent/ad-creative/items/${encodeURIComponent(creativeItemId)}`
    + `/video-audio-candidates/${encodeURIComponent(candidateId)}?${params.toString()}`;
}

export function buildAdCreativeApprovedAudioCandidatePlaybackPath(
  projectId: string,
  creativeItemId: string,
  candidateId: string,
): string {
  const params = new URLSearchParams({ projectId });
  return `/api/agent/ad-creative/items/${encodeURIComponent(creativeItemId)}`
    + `/video-audio-candidates/${encodeURIComponent(candidateId)}/playback?${params.toString()}`;
}

export function buildAdCreativeApprovedAudioCandidateReviewPath(
  projectId: string,
  creativeItemId: string,
  candidateId: string,
): string {
  const params = new URLSearchParams({ projectId });
  return `/api/agent/ad-creative/items/${encodeURIComponent(creativeItemId)}`
    + `/video-audio-candidates/${encodeURIComponent(candidateId)}/review?${params.toString()}`;
}

/** Exact Builder/Edge route; provider and source pins are intentionally absent. */
export function buildAdCreativeApprovedAudioCandidateUploadPath(
  projectId: string,
  creativeItemId: string,
): string {
  return buildAdCreativeApprovedAudioCandidateListPath(projectId, creativeItemId);
}
